import { useContext } from 'react'
import { AbilityContext, useAbilityRoleSchema } from './abilities'
import type { UserRole } from './types'

export const AbilityProvider = ({
    role,
    children,
    roleSchema,
    organizationIds,
    userRoles,
    userId,
}: AbilityProviderProps) => {
    const ability = useAbilityRoleSchema(
        role,
        roleSchema,
        userRoles,
        organizationIds
    )

    return (
        <AbilityContext.Provider
            value={{ ability, organizationIds, role, userRoles, userId }}
        >
            {children}
        </AbilityContext.Provider>
    )
}

export const useAbility = () => useContext(AbilityContext)

// Helper hook to check recreation access with organization
export const useRecreationAccess = () => {
    const { ability, userRoles } = useAbility()

    // Extract organizationIds for recreation-admin or recreation-trainer roles
    const recreationOrgIds =
        userRoles
            ?.filter((role) =>
                ['recreation-admin', 'recreation-trainer'].includes(
                    role.role.key
                )
            )
            .map((role) => role.organizationId)
            .filter((orgId): orgId is string => !!orgId) || []

    const canAccessRecreation = (
        recreation: { organizationId?: string },
        action: string = 'read'
    ) => {
        // Super admin can access everything
        if (ability.can('manage', 'all')) {
            return true
        }

        // Check if user can perform action on recreation for any of their role-based organizationIds
        if (!recreation.organizationId) {
            return ability.can(action, 'Recreation')
        }

        return recreationOrgIds.some(
            (orgId) =>
                ability.can(action, 'Recreation') &&
                orgId === recreation.organizationId
        )
    }

    const canManageRecreation = (recreation: { organizationId?: string }) => {
        return canAccessRecreation(recreation, 'manage')
    }

    const canReadRecreation = (recreation: { organizationId?: string }) => {
        return canAccessRecreation(recreation, 'read')
    }

    return {
        canAccessRecreation,
        canManageRecreation,
        canReadRecreation,
        organizationIds: recreationOrgIds,
    }
}

interface AbilityProviderProps {
    role: string[]
    children: React.ReactNode
    roleSchema: Record<string, unknown>
    organizationIds?: string[]
    userRoles: UserRole[]
    userId?: string | null
}
